import { useRef, useState } from "react";
import { motion, useDragControls } from "framer-motion";
import {
  Type, Pencil, Eraser, Undo2, Trash2, GripVertical, Check, Highlighter, Ban, Pipette,
  Image as ImageIcon, ImagePlus, Sticker, Upload, X,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ── Typography options ────────────────────────────────────────────────────────
export type FontKey = "default" | "serif" | "sans" | "hand" | "mono";

export const FONTS: { key: FontKey; label: string; css: string; preview: string }[] = [
  { key: "default", label: "Default", css: "", preview: "Aa" },
  { key: "serif", label: "Serif", css: "'Newsreader', Georgia, serif", preview: "Aa" },
  { key: "sans", label: "Sans", css: "'DM Sans', system-ui, sans-serif", preview: "Aa" },
  { key: "hand", label: "Hand", css: "'Kalam', cursive", preview: "Aa" },
  { key: "mono", label: "Mono", css: "ui-monospace, 'SF Mono', monospace", preview: "Aa" },
];

export const INK_COLORS = ["#1c1917", "#7c3aed", "#dc2626", "#2563eb", "#059669", "#d97706", "#db2777", "#ffffff"];

// Liquid-glass surface shared by the toolbar and its popovers.
const glass: React.CSSProperties = {
  background: "rgba(255,255,255,0.42)",
  backdropFilter: "blur(22px) saturate(180%)",
  WebkitBackdropFilter: "blur(22px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.6)",
  boxShadow: "0 10px 34px rgba(28,25,23,0.20), inset 0 1px 0 rgba(255,255,255,0.75)",
};

// Resize an image file to a data URL, capped at `max` px on the long edge.
function resizeToDataUrl(file: File, max: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ratio = Math.min(max / img.width, max / img.height, 1);
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      URL.revokeObjectURL(url);
      resolve(dataUrl);
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

interface FloatingToolbarProps {
  // Per-section typography (drives the currently selected section)
  selectedLabel: string | null;
  font: FontKey;
  onFontChange: (f: FontKey) => void;
  textColor: string | null;
  onTextColorChange: (c: string | null) => void;
  scale: number;
  onScaleChange: (n: number) => void;
  onDeselect: () => void;
  // Sketch
  sketchActive: boolean;
  onToggleSketch: () => void;
  penColor: string;
  onPenColorChange: (c: string) => void;
  penSize: number;
  onPenSizeChange: (n: number) => void;
  eraser: boolean;
  onEraserChange: (b: boolean) => void;
  onUndo: () => void;
  onClear: () => void;
  hasStrokes: boolean;
  // Background
  bgImage: string | null;
  onSetBackground: (dataUrl: string | null) => void;
  // Stickers / decorate
  onOpenStickers: () => void;
  // Upload a photo to place on the page (as a movable sticker)
  onUploadImage: (dataUrl: string, fileName: string) => void;
  // Finish editing and leave the entry. Edits themselves are already persisted
  // automatically, so this button is "done", not "commit".
  onSave: () => void;
  /** Live autosave state for the subtle indicator beside the Done button. */
  saveState?: "idle" | "saving" | "saved" | "error";
  // Undo a just-deleted "Next Step"
  stepUndoAvailable?: boolean;
  onStepUndo?: () => void;
  // Mobile: pin the bar to the bottom, non-draggable
  mobile?: boolean;
}

type OpenTool = "text" | "bg" | null;

function IconButton({
  active, onClick, title, children,
}: { active?: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center transition-all",
        active ? "bg-stone-800 text-white shadow-sm" : "text-stone-700 hover:bg-white/60"
      )}
    >
      {children}
    </button>
  );
}

function Swatch({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-6 h-6 rounded-full transition-transform hover:scale-110",
        selected ? "ring-2 ring-offset-1 ring-stone-700" : "ring-1 ring-stone-200"
      )}
      style={{ background: color }}
    />
  );
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] font-sans uppercase tracking-widest text-stone-500 mb-2">{children}</p>
);

export function FloatingToolbar(props: FloatingToolbarProps) {
  const {
    selectedLabel, font, onFontChange, textColor, onTextColorChange, scale, onScaleChange, onDeselect,
    sketchActive, onToggleSketch, penColor, onPenColorChange, penSize, onPenSizeChange,
    eraser, onEraserChange, onUndo, onClear, hasStrokes,
    bgImage, onSetBackground, onOpenStickers, onUploadImage, onSave, saveState,
    stepUndoAvailable, onStepUndo, mobile,
  } = props;

  const [openTool, setOpenTool] = useState<OpenTool>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const bgFileRef = useRef<HTMLInputElement>(null);
  const uploadFileRef = useRef<HTMLInputElement>(null);
  const dragControls = useDragControls();

  return (
    <div ref={layerRef} data-export-hide className="fixed inset-0 z-40 pointer-events-none">
      {/* Centering wrapper keeps a static transform so drag offsets stay clean */}
      <div className={cn("absolute -translate-x-1/2", mobile ? "left-1/2 bottom-5" : "left-1/2 bottom-24")}>
        <motion.div
          drag={!mobile}
          dragListener={false}
          dragControls={dragControls}
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={layerRef}
          className="pointer-events-auto relative"
        >
          {/* ── Text options popover ── */}
          {openTool === "text" && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 rounded-2xl p-3.5" style={glass}>
              {selectedLabel ? (
                <>
                  <div className="mb-3">
                    <span className="text-[11px] font-sans text-stone-600">
                      Styling <span className="font-semibold text-stone-800">{selectedLabel}</span>
                    </span>
                  </div>

                  <Label>Font</Label>
                  <div className="flex gap-1.5 mb-3.5">
                    {FONTS.map((f) => (
                      <button
                        key={f.key}
                        onClick={() => onFontChange(f.key)}
                        title={f.label}
                        className={cn(
                          "flex-1 h-9 rounded-lg text-base flex items-center justify-center transition-all",
                          font === f.key ? "bg-stone-800 text-white" : "bg-white/50 text-stone-700 hover:bg-white/80"
                        )}
                        style={{ fontFamily: f.css || undefined }}
                      >
                        {f.preview}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mb-1">
                    <Label>Size</Label>
                    <span className="text-[10px] font-sans text-stone-500">{Math.round(scale * 100)}%</span>
                  </div>
                  <input
                    type="range" min={0.8} max={1.5} step={0.05} value={scale}
                    onChange={(e) => onScaleChange(Number(e.target.value))}
                    className="w-full mb-3.5 accent-stone-800"
                  />

                  <Label>Color</Label>
                  {mobile && (
                    <div className="flex flex-wrap gap-2 items-center mb-2.5">
                      <button
                        onClick={() => onTextColorChange(null)}
                        title="Default color"
                        className={cn("w-6 h-6 rounded-full flex items-center justify-center transition-transform hover:scale-110 bg-white",
                          textColor === null ? "ring-2 ring-offset-1 ring-stone-700" : "ring-1 ring-stone-200")}
                      >
                        <Ban className="w-3.5 h-3.5 text-stone-400" />
                      </button>
                      {INK_COLORS.map((c) => (
                        <Swatch key={c} color={c} selected={textColor === c} onClick={() => onTextColorChange(c)} />
                      ))}
                      {/* custom picker */}
                      <label title="Custom colour" className="w-6 h-6 rounded-full cursor-pointer ring-1 ring-stone-200 hover:scale-110 transition-transform overflow-hidden relative"
                        style={{ background: "conic-gradient(red,orange,yellow,lime,cyan,blue,magenta,red)" }}>
                        <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => onTextColorChange(e.target.value)} />
                      </label>
                      {typeof (window as unknown as { EyeDropper?: unknown }).EyeDropper !== "undefined" && (
                        <button
                          title="Pick colour from screen"
                          onClick={async () => {
                            const ED = (window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper;
                            try { const r = await new ED().open(); if (r?.sRGBHex) onTextColorChange(r.sRGBHex); } catch { /* cancelled */ }
                          }}
                          className="w-6 h-6 rounded-full flex items-center justify-center ring-1 ring-stone-200 text-stone-600 hover:bg-white/70 hover:scale-110 transition-transform"
                        >
                          <Pipette className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                  {!mobile && (
                    <div className="flex items-start gap-2 rounded-xl bg-white/50 px-3 py-2.5">
                      <Highlighter className="w-4 h-4 text-stone-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] leading-snug text-stone-600 font-sans">
                        <span className="font-semibold text-stone-800">Highlight any text</span> to recolour just that part — a colour bar appears right at your selection.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-6 px-2 text-center">
                  <Type className="w-6 h-6 mx-auto text-stone-400 mb-2" />
                  <p className="text-[13px] font-sans text-stone-600 leading-snug">
                    Tap any section in your entry<br />to style its text.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Background popover ── */}
          {openTool === "bg" && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 rounded-2xl p-3.5" style={glass}>
              <Label>Background photo</Label>
              {bgImage ? (
                <div className="space-y-2.5">
                  <div
                    className="relative rounded-xl overflow-hidden border border-white/50 h-28"
                    style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => bgFileRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg bg-stone-800 text-white text-xs font-sans hover:bg-stone-700 transition-colors"
                    >
                      <Upload className="w-3.5 h-3.5" /> Replace
                    </button>
                    <button
                      onClick={() => onSetBackground(null)}
                      className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg bg-white/60 text-stone-700 text-xs font-sans hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => bgFileRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 bg-white/40 hover:bg-white/70 py-7 transition-all"
                >
                  <ImageIcon className="w-6 h-6 text-stone-500" />
                  <span className="text-xs font-sans text-stone-600">Upload a photo</span>
                </button>
              )}
              <input
                ref={bgFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) resizeToDataUrl(file, 1600, 0.8).then(onSetBackground);
                  e.target.value = "";
                }}
              />
              <p className="text-[10px] text-stone-400 font-sans text-center mt-2.5">Fills the space behind your entry</p>
            </div>
          )}

          {/* ── Sketch options popover ── */}
          {sketchActive && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 rounded-2xl p-3.5" style={glass}>
              <Label>Pen color</Label>
              <div className="flex flex-wrap gap-2 mb-3.5 items-center">
                {INK_COLORS.map((c) => (
                  <Swatch
                    key={c} color={c} selected={!eraser && penColor === c}
                    onClick={() => { onEraserChange(false); onPenColorChange(c); }}
                  />
                ))}
                {/* custom colour picker */}
                <label title="Custom colour" className="w-6 h-6 rounded-full cursor-pointer ring-1 ring-stone-200 hover:scale-110 transition-transform overflow-hidden relative"
                  style={{ background: "conic-gradient(red,orange,yellow,lime,cyan,blue,magenta,red)" }}>
                  <input type="color" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => { onEraserChange(false); onPenColorChange(e.target.value); }} />
                </label>
                {typeof (window as unknown as { EyeDropper?: unknown }).EyeDropper !== "undefined" && (
                  <button
                    title="Pick colour from screen"
                    onClick={async () => {
                      const ED = (window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper;
                      try { const r = await new ED().open(); if (r?.sRGBHex) { onEraserChange(false); onPenColorChange(r.sRGBHex); } } catch { /* cancelled */ }
                    }}
                    className="w-6 h-6 rounded-full flex items-center justify-center ring-1 ring-stone-200 text-stone-600 hover:bg-white/70 hover:scale-110 transition-transform"
                  >
                    <Pipette className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <Label>Brush</Label>
              <div className="flex items-center gap-2 mb-3.5">
                {[2, 4, 8, 14].map((s) => (
                  <button
                    key={s}
                    onClick={() => onPenSizeChange(s)}
                    className={cn(
                      "flex-1 h-9 rounded-lg flex items-center justify-center transition-all",
                      !eraser && penSize === s ? "bg-stone-800" : "bg-white/50 hover:bg-white/80"
                    )}
                    title={`${s}px`}
                  >
                    <span className="rounded-full" style={{ width: s + 2, height: s + 2, background: !eraser && penSize === s ? "#fff" : "#57534e" }} />
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEraserChange(!eraser)}
                  className={cn(
                    "flex-1 h-9 rounded-lg flex items-center justify-center gap-1.5 text-xs font-sans transition-all",
                    eraser ? "bg-stone-800 text-white" : "bg-white/50 text-stone-700 hover:bg-white/80"
                  )}
                >
                  <Eraser className="w-3.5 h-3.5" /> Eraser
                </button>
                <button onClick={onUndo} disabled={!hasStrokes} className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/50 text-stone-700 hover:bg-white/80 disabled:opacity-40 transition-all" title="Undo">
                  <Undo2 className="w-4 h-4" />
                </button>
                <button onClick={onClear} disabled={!hasStrokes} className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/50 text-stone-700 hover:bg-red-50 hover:text-red-500 disabled:opacity-40 transition-all" title="Clear all">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── The bar ── */}
          <div className={cn("flex items-center gap-1 rounded-full py-1.5", mobile ? "px-2" : "pl-1.5 pr-2")} style={glass}>
            {!mobile && (
              <>
                <div
                  onPointerDown={(e) => { e.preventDefault(); dragControls.start(e); }}
                  className="w-6 h-10 flex items-center justify-center cursor-grab active:cursor-grabbing select-none touch-none text-stone-400 hover:text-stone-600"
                  title="Drag to move"
                >
                  <GripVertical className="w-4 h-4" />
                </div>
                <div className="w-px h-6 bg-stone-300/50" />
              </>
            )}

            <IconButton active={openTool === "text"} onClick={() => setOpenTool((t) => (t === "text" ? null : "text"))} title="Text style">
              <Type className="w-5 h-5" />
            </IconButton>

            <IconButton active={sketchActive} onClick={() => { onToggleSketch(); setOpenTool(null); }} title="Sketch">
              <Pencil className="w-5 h-5" />
            </IconButton>

            <IconButton active={openTool === "bg"} onClick={() => setOpenTool((t) => (t === "bg" ? null : "bg"))} title="Background photo">
              <span className="w-5 h-5 rounded-[4px] border-[1.6px] border-current flex items-center justify-center text-[8px] font-bold leading-none tracking-tight">BG</span>
            </IconButton>

            <IconButton onClick={onOpenStickers} title="Stickers & decorate">
              <Sticker className="w-5 h-5" />
            </IconButton>

            <IconButton onClick={() => uploadFileRef.current?.click()} title="Upload a photo">
              <ImagePlus className="w-5 h-5" />
            </IconButton>
            <input
              ref={uploadFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && file.type.startsWith("image/")) {
                  resizeToDataUrl(file, 500, 0.85).then((d) => onUploadImage(d, file.name));
                }
                e.target.value = "";
              }}
            />

            {sketchActive && (
              <IconButton onClick={onToggleSketch} title="Done sketching">
                <Check className="w-5 h-5" />
              </IconButton>
            )}

            {stepUndoAvailable && (
              <>
                <div className="w-px h-6 bg-stone-300/50 mx-0.5" />
                <button
                  onClick={onStepUndo}
                  title="Undo deleted step"
                  className="flex items-center gap-1.5 h-9 pl-2.5 pr-3.5 rounded-full bg-stone-800 text-white text-[13px] font-sans font-medium hover:bg-stone-700 transition-colors"
                >
                  <Undo2 className="w-4 h-4" />
                  Undo
                </button>
              </>
            )}

            <div className="w-px h-6 bg-stone-300/50 mx-0.5" />
            {saveState && saveState !== "idle" && (
              <span
                aria-live="polite"
                className={cn(
                  "hidden sm:inline text-[11px] shrink-0 mr-0.5",
                  saveState === "error" ? "text-destructive" : "text-stone-400",
                )}
              >
                {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : "Not saved"}
              </span>
            )}
            <button
              onClick={onSave}
              title="Done editing"
              className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-[13px] font-semibold hover:opacity-90 transition-opacity shrink-0"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
